// Wire shapes for the session-based attendance API.
//
// These mirror `src/common/types/session.ts` and `pickup-contact.ts` on the
// backend, but live as standalone types so the UI doesn't import server-side
// type packages. Keep them in sync with the backend definitions; any change
// to the wire shape needs an update here too.

export enum SessionStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  ENDED = 'ENDED',
  CANCELLED = 'CANCELLED',
}

export interface SessionShape {
  id: string;
  classId: string;
  className: string;
  locationId: string;
  date: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: SessionStatus;
  expectedStudentIds: string[];
  startedAt?: string;
  startedBy?: string;
  startedByName?: string;
  endedAt?: string;
  endedBy?: string;
  endedByName?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelledReason?: string;
  createdAt: string;
  updatedAt: string;
}

// The trimmed shape returned by GET /sessions/live — just what the dashboard
// strip needs to render a live session and offer "open". Mirrors the projection
// in src/attendance/handlers/list-live-sessions/index.ts.
export interface LiveSessionShape {
  id: string;
  classId: string;
  className: string;
  date: string;
  scheduledStart: string;
  scheduledEnd: string;
  startedAt?: string;
  startedByName?: string;
  expectedCount: number;
  presentCount: number;
}

export enum AttendanceEventType {
  CHECK_IN = 'CHECK_IN',
  CHECK_OUT = 'CHECK_OUT',
  ROLLED_OUT = 'ROLLED_OUT',
  ROLLED_IN = 'ROLLED_IN',
}

export enum AttendanceEventSource {
  STAFF_TAP = 'STAFF_TAP',
  PARENT_TAP = 'PARENT_TAP',
  AUTO_ROLL_FORWARD = 'AUTO_ROLL_FORWARD',
  AUTO_END_OF_SESSION = 'AUTO_END_OF_SESSION',
  ADMIN_EDIT = 'ADMIN_EDIT',
}

export interface ReleasedToShape {
  contactId?: string;
  name: string;
  relationship?: string;
  verificationNote?: string;
  flagged: boolean;
}

/** What the staff page sends when checking out — either a real contact id
 *  or a free-text "Other" with mandatory verification note. */
export type ReleasedToInput =
  | { contactId: string }
  | { name: string; relationship?: string; verificationNote: string };

export interface AttendanceEventShape {
  id: string;
  sessionId: string;
  classId: string;
  locationId: string;
  date: string;
  studentId: string;
  studentName: string;
  type: AttendanceEventType;
  at: string;
  by: string;
  byName: string;
  source: AttendanceEventSource;
  releasedTo?: ReleasedToShape;
  /** S3 key of an optional photo snapped by staff at pickup. Fetch a
   *  presigned GET URL from /uploads/view when you need to render it. */
  pickupPhotoS3Key?: string;
  notes?: string;
  voidedAt?: string;
  voidedBy?: string;
  voidedReason?: string;
  createdAt: string;
}

export enum StudentDayStatus {
  EXPECTED = 'EXPECTED',
  HERE = 'HERE',
  GONE = 'GONE',
  /** Session ended with the child still checked in; nobody has recorded a collection yet. Not a pickup. */
  SESSION_ENDED = 'SESSION_ENDED',
  NO_SHOW = 'NO_SHOW',
  PLANNED_ABSENT = 'PLANNED_ABSENT',
}

export interface StudentDaySpanShape {
  sessionIds: string[];
  className: string;
  inAt: string;
  outAt?: string;
}

export interface StudentDayStateShape {
  studentId: string;
  studentName: string;
  date: string;
  status: StudentDayStatus;
  firstCheckInAt?: string;
  firstCheckInBy?: string;
  lastCheckOutAt?: string;
  lastCheckOutBy?: string;
  spans: StudentDaySpanShape[];
  updatedAt: string;
}

export enum PickupContactRelationship {
  PARENT = 'PARENT',
  GUARDIAN = 'GUARDIAN',
  GRANDPARENT = 'GRANDPARENT',
  AUNT_UNCLE = 'AUNT_UNCLE',
  SIBLING = 'SIBLING',
  FAMILY_FRIEND = 'FAMILY_FRIEND',
  NEIGHBOUR = 'NEIGHBOUR',
  OTHER = 'OTHER',
}

export interface PickupContactShape {
  id: string;
  studentId: string;
  name: string;
  relationship: string;
  relationshipType: PickupContactRelationship;
  phone?: string;
  isGuardian: boolean;
  isPrimary: boolean;
  addedBy: string;
  addedByName: string;
  addedByRole: string;
  addedAt: string;
  isActive: boolean;
}

/** Account-level grouped contact as returned by GET /parents/me/pickup-contacts. */
export interface PickupContactGroupShape {
  groupId: string;
  name: string;
  relationship: string;
  relationshipType: PickupContactRelationship;
  phone?: string;
  isGuardian: boolean;
  students: { id: string; firstName?: string; lastName?: string }[];
  isLegacy: boolean;
}

// ---------------------------------------------------------------------------
// Tusla attendance register — mirrors of `src/attendance/utils/register.ts`
// (RegisterEntry / RegisterSession / RegisterDay / AttendanceConcern) plus the
// handler's response envelopes. Field names must match the server exactly.
// ---------------------------------------------------------------------------

export type RegisterEntryState = 'PRESENT' | 'PLANNED_ABSENT' | 'UNEXPLAINED';

/** Why a check-out deserves a look: a release to someone NOT on the pickup
 *  list (safeguarding) vs. a session that ended with no sign-out tapped
 *  (record-keeping). The server sets `flaggedPickup` for both; this says which. */
export type RegisterPickup = 'OTHER_COLLECTOR' | 'NOT_SIGNED_OUT';

export interface RegisterEntry {
  studentId: string;
  state: RegisterEntryState;
  /** Earliest non-voided CHECK_IN/ROLLED_IN instant. */
  checkInAt?: string;
  /** Any non-voided CHECK_OUT whose releasedTo is flagged — auto close-outs
   *  included. Kept for older servers; `pickup` carries the cause. */
  flaggedPickup?: boolean;
  /** Set when a check-out needs attention; see RegisterPickup. */
  pickup?: RegisterPickup;
  /** Staff code of whoever signed the child in; absent for a roll-in, a
   *  parent tap, or an older server. */
  checkInBy?: string;
  /** The arrival was a roll-in from the previous class — nobody to credit. */
  checkInKind?: 'ROLLED_IN';
  /** Time of the last staff or parent sign-out; an auto close-out sets none. */
  checkOutAt?: string;
  /** Staff code for `checkOutAt` — only when staff tapped it. */
  checkOutBy?: string;
  /** Signed or rolled in without being booked on this class-date. Always
   *  PRESENT; never an expected session. Absent from older servers. */
  unexpected?: true;
}

/** A name and when they did it, e.g. who opened the register. */
export interface RegisterActor {
  name: string;
  at: string;
}

export interface RegisterSession {
  classId: string;
  className: string;
  date: string;
  /** Absent when no session row was ever materialised for the class-date. */
  sessionId?: string;
  /** Closure/cancelled day: shown so the register has no silent gaps, but it
   *  carries no entries and is never counted as an expected session. */
  suppressed?: { reason: string };
  /** One per EXPECTED child, planned-absent children included; then one per
   *  child who was in without a booking (`unexpected`), by arrival. */
  entries: RegisterEntry[];
  /** The session's room snapshot, else the class's current room. */
  room?: string;
  /** Names from the session's assigned-staff snapshot (`[]` = nobody);
   *  absent when the session predates the snapshot or never existed. */
  assignedStaff?: string[];
  /** Everyone who tapped this class-date as a person, with their code. */
  staffOnDay?: Array<{ code: string; name: string }>;
  openedBy?: RegisterActor;
  closedBy?: RegisterActor;
}

export interface RegisterDay {
  date: string;
  /** Scheduled classes only; empty means no classes ran that day. */
  sessions: RegisterSession[];
}

/** GET /locations/{locationId}/attendance-register?from&to&classId */
export interface AttendanceRegisterResponse {
  from: string;
  to: string;
  days: RegisterDay[];
  students: { id: string; name: string }[];
}

export interface AttendanceConcern {
  studentId: string;
  name: string;
  expectedSessions: number;
  /** Zero by definition of the concern. */
  attendedSessions: number;
  /** Expected sessions covered by a filed absence. */
  plannedAbsences: number;
  /** Every expected session in the window had a filed absence. */
  allExplained: boolean;
  /** The child's earliest expected session date in the window. */
  firstExpectedDate: string;
  lastAttendedDate?: string;
}

/** GET /locations/{locationId}/attendance-concerns?windowDays */
export interface AttendanceConcernsResponse {
  window: {
    from: string;
    to: string;
    days: number;
    /** First date any class ran in the window; absent when none did. */
    firstClassDay?: string;
  };
  concerns: AttendanceConcern[];
}

/** GET /students/{studentId}/attendance-history?from&to */
export interface StudentAttendanceHistoryResponse {
  from: string;
  to: string;
  events: AttendanceEventShape[];
}
